import React, { useEffect, useRef, useState } from "react";
import { IoPower } from "react-icons/io5";
import { DEVICE_TYPES as T, typeOf } from "../../constants/devices";
import { UserService } from "../../services/UserService";
import { User } from "../../storage/store";
import { apiMessage } from "../../api/apiMessage";
import { useToggleTop } from "../../components/common/ToggleTop";
import { switchFeedback } from "../../preferences/feedback";

// Left out, as in the app (app/helpers/bulkControl.js): sensors only report,
// and IR blasters have no on/off of their own. The API decides about the rest.
const NOT_SWITCHED = [T.DW_SENSOR, T.PIR_SENSOR, T.GAS_SENSOR, T.SMOKE_DETECTOR, T.IRBM];

// Each fan switches on at its last speed, as the old all on/off did. Only
// whole numbers 0-10 go; the API leaves out a fan with no speed and says so.
const speedsOf = (devices) =>
  Object.fromEntries(
    devices
      .filter((device) => device.props && "speed" in device.props)
      .map((device) => [device.applianceId, Number(device.props.speed)])
      .filter(([, speed]) => Number.isInteger(speed) && speed >= 0 && speed <= 10)
  );

// The devices the API didn't switch (locks, gates, devices that need a colour
// or a speed, ...), named in one short line, as the app does. Null when it
// switched them all.
const skippedLine = (response, devices) => {
  const skipped = (response?.devices ?? []).filter((item) => !item.ok);
  if (!skipped.length) return null;
  const nameOf = (id) => devices.find((device) => device.applianceId === id)?.name || "A device";
  const first = nameOf(skipped[0].applianceId);
  if (skipped.length === 1) return `${first} wasn't switched.`;
  const second = skipped.length === 2 ? nameOf(skipped[1].applianceId) : `${skipped.length - 1} more`;
  return `${first} and ${second} weren't switched.`;
};

// The Devices tab's and a room's power button (app/components/TabBar/TopTabBar.js,
// app/screens/RoomDetails.js): it opens "all off" and "all on" for the devices
// in the open tab or room, sent in one request to the API's all on/off route.
// The API leaves out what it can't switch this way and says which.
const BulkPower = ({ devices, onDone, scope = "tab" }) => {
  const toast = useToggleTop();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const targets = devices.filter((device) => device.applianceId && !NOT_SWITCHED.includes(typeOf(device)));

  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (event.type === "keydown" ? event.key === "Escape" : !ref.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", close);
    };
  }, [open]);

  if (!targets.length) return null;

  const switchAll = async (on) => {
    setOpen(false);
    setBusy(true);
    switchFeedback(on);
    const state = on ? "on" : "off";
    try {
      const response = await UserService.bulkControl(
        User.getPropertyId(),
        state,
        targets.map((device) => device.applianceId),
        on ? speedsOf(targets) : undefined
      );
      const sent = Number(response?.sent) || 0;
      const skipped = skippedLine(response, targets);
      const switched = sent ? `Switched ${sent} ${sent === 1 ? "device" : "devices"} ${state}.` : "";
      toast([switched, skipped].filter(Boolean).join(" ") || `Switched the devices ${state}.`, skipped ? "blue" : "green");
      setTimeout(onDone, 1000);
    } catch (error) {
      toast(apiMessage(error, `Couldn't switch the devices ${state}. Please try again.`), "red");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* Room under the last cards, so the button doesn't cover them. */}
      <div className="hx-bulk-space" aria-hidden="true" />
      <div className="hx-bulk" ref={ref}>
        {open ? (
          <div className="hx-bulk-actions" role="menu" aria-label={`Switch every device in this ${scope}`}>
            <button type="button" role="menuitem" className="hx-bulk-action is-off" onClick={() => switchAll(false)}>
              <span>All off</span>
              <IoPower aria-hidden="true" />
            </button>
            <button type="button" role="menuitem" className="hx-bulk-action is-on" onClick={() => switchAll(true)}>
              <span>All on</span>
              <IoPower aria-hidden="true" />
            </button>
          </div>
        ) : null}
        <button
          type="button"
          className="hx-bulk-fab"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={`Switch every device in this ${scope}`}
          disabled={busy}
          onClick={() => setOpen(!open)}
        >
          <IoPower aria-hidden="true" />
        </button>
      </div>
    </>
  );
};

export default BulkPower;
